package com.sweetlime.dungeonamigo.presentation.features.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.presentation.features.auth.AuthScreen
import com.sweetlime.dungeonamigo.presentation.features.character.CharacterSheetScreen
import com.sweetlime.dungeonamigo.presentation.ui.components.*
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.classColorFor
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import org.koin.compose.koinInject

object ProfileScreen : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: ProfileViewModel = koinInject()
        val profile by vm.userProfile.collectAsState()
        val characters by vm.characters.collectAsState()
        val favoriteClass by vm.favoriteClass.collectAsState()
        val signedOut by vm.signedOut.collectAsState()

        var showAvatarPicker by remember { mutableStateOf(false) }
        var showPortraitPicker by remember { mutableStateOf(false) }
        var showDeleteDialog by remember { mutableStateOf(false) }
        var editingUsername by remember { mutableStateOf(false) }
        var usernameInput by remember(profile?.username) { mutableStateOf(profile?.username ?: "") }

        LaunchedEffect(signedOut) {
            if (signedOut) navigator.replace(AuthScreen())
        }

        val sf = silkFont()
        val pf = pixelFont()

        Box(modifier = Modifier.fillMaxSize().background(BrandColors.Vault)) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 40.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                // ── Hero Block ────────────────────────────────────────────────
                item {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(BrandColors.Stone)
                            .padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        // 104dp avatar tile with Parchment tint + badge
                        Box(contentAlignment = Alignment.BottomEnd) {
                            CharacterPortrait(
                                characterClass = "adventurer",
                                avatarId = profile?.avatarId ?: "",
                                primaryColor = BrandColors.Parchment,
                                secondaryColor = Color(profile?.portraitSecondaryColor ?: 0xFF4A90D9L),
                                size = PortraitSize.LARGE,
                                modifier = Modifier.clickable { showAvatarPicker = true },
                            )
                            Box(
                                modifier = Modifier
                                    .size(22.dp)
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(BrandColors.Ember),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text("✦", color = BrandColors.Ivory, fontSize = 10.sp)
                            }
                        }

                        // Change avatar / Customize colors links
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            TextButton(onClick = { showAvatarPicker = true }) {
                                Text("Change Avatar", color = BrandColors.SpellBlue, fontSize = 12.sp)
                            }
                            Text("·", color = BrandColors.IvoryDim, fontSize = 12.sp)
                            TextButton(onClick = { showPortraitPicker = true }) {
                                Text("Customize Colors", color = BrandColors.IvoryDim, fontSize = 12.sp)
                            }
                        }

                        // Username (editable)
                        val focusRequester = remember { FocusRequester() }
                        val focusManager = LocalFocusManager.current
                        if (editingUsername) {
                            OutlinedTextField(
                                value = usernameInput,
                                onValueChange = { usernameInput = it },
                                singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor   = BrandColors.Ember,
                                    unfocusedBorderColor = BrandColors.Slate.copy(alpha = 0.4f),
                                    focusedTextColor     = BrandColors.Ivory,
                                    unfocusedTextColor   = BrandColors.Ivory,
                                    cursorColor          = BrandColors.Parchment,
                                ),
                                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                                keyboardActions = KeyboardActions(onDone = {
                                    vm.updateUsername(usernameInput)
                                    editingUsername = false
                                    focusManager.clearFocus()
                                }),
                                modifier = Modifier.fillMaxWidth().focusRequester(focusRequester),
                            )
                            LaunchedEffect(Unit) { focusRequester.requestFocus() }
                            TextButton(onClick = {
                                vm.updateUsername(usernameInput)
                                editingUsername = false
                                focusManager.clearFocus()
                            }) {
                                Text("Done", color = BrandColors.Parchment)
                            }
                        } else {
                            Text(
                                text = profile?.username?.ifBlank { "Adventurer" } ?: "Adventurer",
                                color = BrandColors.Ivory,
                                fontFamily = pf,
                                fontSize = 27.sp,
                                fontWeight = FontWeight.SemiBold,
                                modifier = Modifier.clickable { editingUsername = true },
                            )
                        }

                        Text(
                            text = profile?.email ?: "",
                            color = BrandColors.IvoryDim,
                            fontSize = 13.5.sp,
                        )

                        if (profile?.tier == "plus") {
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(20.dp))
                                    .background(BrandColors.Parchment.copy(alpha = 0.15f))
                                    .border(1.dp, BrandColors.Parchment.copy(alpha = 0.4f), RoundedCornerShape(20.dp))
                                    .padding(horizontal = 12.dp, vertical = 4.dp),
                            ) {
                                Text("✦ Amigo+", color = BrandColors.Parchment, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                }

                // ── Stat Tiles ───────────────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 18.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        StatTile(value = "${characters.size}", label = "Characters", modifier = Modifier.weight(1f))
                        StatTile(value = "${profile?.sessionsPlayed ?: 0}", label = "Sessions", modifier = Modifier.weight(1f))
                        StatTile(value = favoriteClass.take(8).ifBlank { "—" }, label = "Fav. Class", modifier = Modifier.weight(1f))
                    }
                }

                // ── Characters Section ───────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 18.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        SectionTitle(title = "Characters")
                        if (characters.isNotEmpty()) {
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(12.dp))
                                    .background(BrandColors.Ember)
                                    .padding(horizontal = 8.dp, vertical = 3.dp),
                            ) {
                                Text("${characters.size}", color = BrandColors.Ivory, fontSize = 12.sp, fontWeight = FontWeight.Bold, fontFamily = pf)
                            }
                        }
                    }
                }

                if (characters.isEmpty()) {
                    item {
                        EmptyStateView(
                            illustration = { ProfileEmptyCharactersIllustration() },
                            headline = "No characters yet",
                            subtext = "Your characters will appear here",
                            modifier = Modifier.fillMaxWidth().padding(32.dp),
                        )
                    }
                } else {
                    item {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 18.dp)
                                .clip(RoundedCornerShape(14.dp))
                                .background(BrandColors.Stone)
                                .border(1.dp, BrandColors.Slate.copy(alpha = 0.4f), RoundedCornerShape(14.dp)),
                        ) {
                            characters.forEachIndexed { index, character ->
                                MiniCharRow(
                                    character = character,
                                    onClick = { navigator.push(CharacterSheetScreen(character.id)) },
                                )
                                if (index < characters.lastIndex) {
                                    HorizontalDivider(
                                        color = BrandColors.Slate.copy(alpha = 0.2f),
                                        modifier = Modifier.padding(horizontal = 12.dp),
                                    )
                                }
                            }
                        }
                    }
                }

                // ── Settings Section ─────────────────────────────────────────
                item {
                    SectionTitle(title = "Settings", modifier = Modifier.padding(horizontal = 18.dp))
                }

                item {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 18.dp)
                            .clip(RoundedCornerShape(14.dp))
                            .background(BrandColors.Stone)
                            .border(1.dp, BrandColors.Slate.copy(alpha = 0.4f), RoundedCornerShape(14.dp)),
                    ) {
                        SettingsRow(label = "Edit Username", onClick = { editingUsername = true })
                        HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 12.dp))
                        SettingsRow(label = "Sign Out", onClick = { vm.signOut() })
                        HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 12.dp))
                        SettingsRow(label = "Delete Account", onClick = { showDeleteDialog = true }, labelColor = BrandColors.Ember)
                    }
                }

                // ── Footer ───────────────────────────────────────────────────
                item {
                    Text(
                        text = "DUNGEON AMIGO · v0.4.0",
                        color = BrandColors.Slate,
                        fontFamily = sf,
                        fontSize = 9.sp,
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 18.dp, vertical = 8.dp),
                    )
                }
            }
        }

        if (showAvatarPicker) {
            AvatarPickerSheet(
                currentAvatarId = profile?.avatarId ?: "",
                onSelect = { avatarId -> vm.updateAvatarId(avatarId); showAvatarPicker = false },
                onDismiss = { showAvatarPicker = false },
            )
        }

        if (showPortraitPicker && profile != null) {
            PortraitPickerSheet(
                characterClass = "adventurer",
                initialPrimary = profile!!.portraitPrimaryColor,
                initialSecondary = profile!!.portraitSecondaryColor,
                onSave = { primary, secondary -> vm.updatePortraitColors(primary, secondary) },
                onDismiss = { showPortraitPicker = false },
            )
        }

        if (showDeleteDialog) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                containerColor = BrandColors.Stone,
                title = { Text("Delete Account", color = BrandColors.Ember, fontWeight = FontWeight.Bold) },
                text = {
                    Text(
                        "This will permanently delete your account and all characters. This cannot be undone.",
                        color = BrandColors.IvoryDim,
                    )
                },
                confirmButton = {
                    Button(
                        onClick = { vm.deleteAccount(); showDeleteDialog = false },
                        colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                    ) {
                        Text("Delete", color = BrandColors.Ivory, fontWeight = FontWeight.Bold)
                    }
                },
                dismissButton = {
                    OutlinedButton(
                        onClick = { showDeleteDialog = false },
                        border = androidx.compose.foundation.BorderStroke(1.dp, BrandColors.Slate),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.IvoryDim),
                    ) {
                        Text("Cancel")
                    }
                },
            )
        }
    }
}

@Composable
private fun StatTile(value: String, label: String, modifier: Modifier = Modifier) {
    val pf = pixelFont()
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(BrandColors.Stone)
            .border(1.dp, BrandColors.Slate.copy(alpha = 0.33f), RoundedCornerShape(12.dp))
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(value, color = BrandColors.Parchment, fontFamily = pf, fontSize = 24.sp, fontWeight = FontWeight.SemiBold)
        Text(label, color = BrandColors.IvoryDim, fontSize = 11.5.sp)
    }
}

@Composable
private fun MiniCharRow(character: Character, onClick: () -> Unit) {
    val pf = pixelFont()
    val hpFraction = if (character.maxHp > 0) character.currentHp.toFloat() / character.maxHp else 0f
    val hpColor = when {
        hpFraction > 0.5f  -> BrandColors.Ember
        hpFraction > 0.25f -> BrandColors.EmberAmber
        else               -> BrandColors.EmberDim
    }
    val classColor = classColorFor(character.characterClass)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        AvatarTile(character = character, classColor = classColor, size = 42.dp)

        Column(Modifier.weight(1f)) {
            Text(
                character.name.ifBlank { "Unnamed" },
                color = BrandColors.Ivory,
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
            )
            val sub = buildString {
                if (character.race.isNotBlank()) append(character.race)
                if (character.characterClass.isNotBlank()) {
                    if (isNotEmpty()) append(" · ")
                    append(character.characterClass)
                }
                if (character.level > 0) append(" Lv.${character.level}")
            }
            if (sub.isNotBlank()) Text(sub, color = BrandColors.IvoryDim, fontSize = 12.sp)
        }

        Column(horizontalAlignment = Alignment.End) {
            Row(verticalAlignment = Alignment.Bottom) {
                Text("${character.currentHp}", color = hpColor, fontFamily = pf, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                Text("/${character.maxHp}", color = BrandColors.IvoryDim, fontSize = 11.sp)
            }
            Text("HP", color = BrandColors.IvoryDim, fontSize = 9.sp)
        }
    }
}

@Composable
private fun SettingsRow(label: String, onClick: () -> Unit, labelColor: Color = BrandColors.Ivory) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(label, color = labelColor, fontSize = 15.sp)
        Icon(Icons.Filled.ChevronRight, contentDescription = null, tint = BrandColors.Slate, modifier = Modifier.size(20.dp))
    }
}
